import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate } from 'react-router-dom';

interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type?: string;
  especialidad?: string;
  auth_method: string;
  picture?: string;
}

const ProfessionalDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    
    if (!userData || loginStatus !== 'true') {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      
      if (!parsedUser.es_profesional) {
        navigate('/dashboard');
        return;
      }
      
      setUser(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const getInitials = (nombre: string, apellido: string) => {
    return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
  };

  const handleCompleteProfile = () => {
    navigate('/professional/setup');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleProfileConfig = () => {
    navigate('/professional/profile-config');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size={56} label="Cargando dashboard profesional..." />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Título de bienvenida */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenido/a {user.nombre}! 👨u200d💼
          </h1>
          <p className="text-gray-600">
            Dashboard profesional{user.especialidad && ` - ${user.especialidad}`}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-2m-2 0H7m5 0v-5a2 2 0 00-2-2H8a2 2 0 00-2 2v5m5 0V9a1 1 0 00-1-1H9a1 1 0 00-1 1v10m4 0V4a1 1 0 00-1-1h-1a1 1 0 00-1 1v3M9 21v-4a1 1 0 011-1h1a1 1 0 011 1v4" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Dashboard General</h2>
              <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                Para una experiencia más personalizada y acceder a funcionalidades específicas 
                de tu profesión, completa tu perfil profesional. Esto te permitirá acceder a 
                herramientas especializadas para tu área de trabajo.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleCompleteProfile}
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-shadow flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Completar Perfil Profesional</span>
                </button>
                
                <button
                  onClick={handleGoToDashboard}
                  className="bg-gray-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>Dashboard Principal</span>
                </button>

                <button
                  onClick={handleProfileConfig}
                  className="bg-blue-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Configurar Perfil</span>
                </button>
              </div>
            </div>

            {/* Features preview */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">🏥</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Profesionales de la Salud</h3>
                <p className="text-sm text-gray-600">
                  Dashboard especializado con gestión de pacientes, historiales médicos, 
                  recetas digitales y más.
                </p>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">💅</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Profesionales de la Belleza</h3>
                <p className="text-sm text-gray-600">
                  Dashboard con portfolio visual, gestión de citas, herramientas de marketing 
                  y seguimiento de clientes.
                </p>
              </div>
            </div>

            {/* Quick actions grid */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => navigate('/professional/portfolio')}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 active:scale-95 transition-transform"
                >
                  <div className="text-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <span className="text-purple-600">📂</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Portfolio</span>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/professional/clients')}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 active:scale-95 transition-transform"
                >
                  <div className="text-center">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <span className="text-green-600">👥</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Clientes</span>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/professional/calendar')}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 active:scale-95 transition-transform"
                >
                  <div className="text-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <span className="text-blue-600">📅</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">Calendario</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Current status */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado del Perfil</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Información básica</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    ✓ Completo
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tipo de profesional</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                    ⚠ Pendiente
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Especialidad</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                    ⚠ Pendiente
                  </span>
                </div>

                <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                  <div className="bg-indigo-500 h-2 rounded-full" style={{ width: '33%' }}></div>
                </div>
                <p className="text-xs text-gray-500 text-center">33% completado</p>
              </div>
            </div>

            {/* Quick actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
              <div className="space-y-3">
                <button 
                  onClick={handleCompleteProfile}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                >
                  <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-sm text-gray-700">Completar perfil</span>
                </button>

                <button 
                  onClick={handleProfileConfig}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                >
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm text-gray-700">Configurar perfil</span>
                </button>

                <button 
                  onClick={() => navigate('/professional/settings')}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm text-gray-700">Configuración general</span>
                </button>

                <button 
                  onClick={() => navigate('/help')}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3"
                >
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-gray-700">Centro de ayuda</span>
                </button>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="font-semibold text-indigo-800 mb-2">💡 Consejo</h4>
              <p className="text-sm text-indigo-700">
                Completar tu perfil profesional te permitirá acceder a herramientas 
                especializadas y una mejor experiencia personalizada.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ NUEVO: Estilos optimizados para móvil */}
      <style>{`
        /* Deshabilitar efectos hover en dispositivos táctiles */
        @media (hover: none) {
          .hover\\:shadow-md:hover,
          .hover\\:shadow-lg:hover,
          .hover\\:bg-gray-50:hover,
          .hover\\:bg-gray-600:hover,
          .hover\\:bg-blue-600:hover {
            box-shadow: none !important;
            background-color: inherit !important;
          }
        }

        /* Optimizar transiciones solo para dispositivos con hover */
        @media (hover: hover) {
          .transition-shadow {
            transition: box-shadow 0.15s ease;
          }
          .transition-colors {
            transition: background-color 0.15s ease, color 0.15s ease;
          }
        }

        /* En móvil, remover todas las transiciones de sombra */
        @media (hover: none) {
          .transition-shadow {
            transition: none !important;
          }
        }

        /* Smooth scroll optimizado */
        * {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>
    </div>
  );
};

export default ProfessionalDashboard;